/**
 * Mezclador del formato mixto (fase 2): la lógica pura de las filas «De la
 * plantilla / Del original» y de qué partes del editor de plantillas no se
 * usan con la mezcla elegida. Sin React: lo usan FilasAspectos y el
 * formulario, y se prueba sin navegador.
 */
import {
  ASPECTOS, ASPECTOS_DISPONIBLES, ajustarCoherencia, chocaConCandados, normalizarAspectos, todoOriginal,
  type Aspecto, type AspectosFormato, type OrigenAspecto,
} from './aspectos';

/** Lo que se anuncia cuando un cambio arrastra otras filas (texto `tf.mixto…`). */
export type AnuncioCoherencia = 'hojaConMargenes' | 'orientacionConHoja';

/**
 * Por qué una fila NO pasa a `origen` (el botón se atenúa y el motivo se
 * dice): `fija` = la fase 2 aún no lo toma de la plantilla; `bloqueada` = la
 * norma obliga a tomarlo de la plantilla; `arrastraBloqueo` = el cambio
 * arrastraría (por coherencia) un aspecto con candado; `todoOriginal` = todo
 * quedaría «Del original» (para eso está «Original» en la orden). null si el
 * cambio se aplica o la fila ya vale eso.
 */
export type MotivoSinCambio = 'fija' | 'bloqueada' | 'arrastraBloqueo' | 'todoOriginal';
export function porQueNoCambia(a: AspectosFormato, aspecto: Aspecto, origen: OrigenAspecto, bloqueados: readonly Aspecto[]): MotivoSinCambio | null {
  if (a[aspecto] === origen) return null;
  if (!ASPECTOS_DISPONIBLES.includes(aspecto)) return 'fija';
  if (origen === 'original' && bloqueados.includes(aspecto)) return 'bloqueada';
  const nuevo = ajustarCoherencia({ ...a, [aspecto]: origen }, aspecto);
  if (chocaConCandados(nuevo, bloqueados)) return 'arrastraBloqueo';
  return todoOriginal(nuevo) ? 'todoOriginal' : null;
}

/**
 * Cambia una fila. Devuelve null si el cambio no se aplica: el valor que ya
 * tenía o cualquier motivo de porQueNoCambia. El resultado ya va
 * normalizado (todo de la plantilla = null = formato completo).
 */
export function cambiarAspecto(
  a: AspectosFormato,
  aspecto: Aspecto,
  origen: OrigenAspecto,
  bloqueados: readonly Aspecto[],
): { aspectos: AspectosFormato | null; anuncio: AnuncioCoherencia | null } | null {
  if (a[aspecto] === origen || porQueNoCambia(a, aspecto, origen, bloqueados) !== null) return null;
  const nuevo = ajustarCoherencia({ ...a, [aspecto]: origen }, aspecto);
  const arrastra = ASPECTOS.some((k) => k !== aspecto && nuevo[k] !== a[k]);
  let anuncio: AnuncioCoherencia | null = null;
  if (arrastra) {
    anuncio = (aspecto === 'hoja' && origen === 'plantilla') || (aspecto === 'margenes' && origen === 'original')
      ? 'hojaConMargenes'
      : 'orientacionConHoja';
  }
  const n = normalizarAspectos(nuevo);
  return { aspectos: n.ok ? n.valor : nuevo, anuncio };
}

/** La mezcla que manda en el editor; null = formato completo (o el mixto no se ofrece). */
export function mezclaDelFormulario(
  v: { aspectos?: unknown; mirrorsSource?: boolean; lineNumbering?: boolean },
  mixtoActivo: boolean,
): AspectosFormato | null {
  if (!mixtoActivo || v.mirrorsSource || v.lineNumbering) return null;
  const n = normalizarAspectos(v.aspectos);
  return n.ok ? n.valor : null;
}

/** Partes del editor de plantillas que no se usan con esta mezcla (se atenúan; sus valores se quedan). */
export type BloqueFormulario =
  | 'hoja' | 'orientacion' | 'margenes' | 'tipografia' | 'encabezado' | 'pie' | 'distancias' | 'guiones' | 'cierre'
  | 'seccionPagina' | 'seccionBandas';

export function bloquesDelOriginal(a: AspectosFormato | null): Set<BloqueFormulario> {
  const s = new Set<BloqueFormulario>();
  if (!a) return s;
  const orig = (k: Aspecto) => a[k] === 'original';
  for (const k of ['hoja', 'orientacion', 'margenes', 'tipografia', 'encabezado', 'pie', 'guiones', 'cierre'] as const) {
    if (orig(k)) s.add(k);
  }
  // Las distancias del encabezado y del pie colocan CUALQUIER banda (también
  // la del original) cuando los márgenes son de la plantilla: van con ellos.
  // Con la tipografía de la plantilla (reflujo) es el formato completo: sus
  // bandas van a sus distancias aunque los márgenes sean del original.
  if (orig('margenes') && orig('tipografia')) s.add('distancias');
  if (orig('hoja') && orig('orientacion') && orig('margenes') && orig('tipografia')) s.add('seccionPagina');
  if (orig('encabezado') && orig('pie') && orig('margenes')) s.add('seccionBandas');
  return s;
}

/**
 * Asistente de la orden: ¿se ofrece el selector de hoja (y de orientación)
 * de la orden? Sin plantilla, sí. Con una plantilla, solo si es mixta y
 * conserva la hoja del original; la orientación, además, si también es del
 * original (por coherencia, siempre lo es cuando la hoja lo es).
 */
export function selectorHojaDeLaOrden(
  formato: { esMixta?: boolean; aspectos?: unknown } | null | undefined,
  hayFormato: boolean,
): { hoja: boolean; orientacion: boolean } {
  if (!hayFormato) return { hoja: true, orientacion: true };
  const n = formato?.esMixta ? normalizarAspectos(formato.aspectos) : null;
  const a = n?.ok ? n.valor : null;
  const hoja = !!a && a.hoja === 'original';
  return { hoja, orientacion: hoja && a?.orientacion === 'original' };
}
